// src/views/hrms/attendance/AttendancePage.jsx
import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { todayISO } from "./utils/attendanceUtils";
import useAttendanceData from "./hooks/useAttendanceData";

import AttendanceHeader   from "./components/AttendanceHeader";
import KpiCards           from "./components/KpiCards";
import LoginRangePanel    from "./components/LoginRangePanel";
import AttendanceFilters  from "./components/AttendanceFilters";
import AttendanceTable    from "./components/AttendanceTable";
import ExportModal        from "./components/ExportModal";

export default function AttendancePage() {
  const nav = useNavigate();
  const defaultDate = useMemo(() => todayISO(), []);

  // ── filter state ──────────────────────────────────────────────────────────
  const [statusFilter, setStatusFilter] = useState("All");
  const [rangeFilter,  setRangeFilter]  = useState("All");
  const [sortBy,       setSortBy]       = useState("last7");
  const [searchRaw,    setSearchRaw]    = useState("");
  const [search,       setSearch]       = useState("");
  const [perPage,      setPerPage]      = useState(10);
  const [page,         setPage]         = useState(1);
  const [dateFrom,     setDateFrom]     = useState(defaultDate);
  const [dateTo,       setDateTo]       = useState(defaultDate);

  // ── export modal state ────────────────────────────────────────────────────
  const [exportOpen, setExportOpen] = useState(false);

  // ── debounce search ───────────────────────────────────────────────────────
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchRaw.trim()), 250);
    return () => clearTimeout(t);
  }, [searchRaw]);

  // ── data + derived values from hook ───────────────────────────────────────
  const {
    loading,
    sortedDesc,
    pageRows,
    totalPages,
    start,
    officeCenter,
    rangeCounts,
    kpi,
    NEAR_KM,
    CLOSE_KM,
    MOD_KM,
  } = useAttendanceData({
    statusFilter,
    rangeFilter,
    search,
    dateFrom,
    dateTo,
    page,
    perPage,
  });

  // ── keep page in bounds ───────────────────────────────────────────────────
  useEffect(() => {
    if (page > totalPages) setPage(1);
  }, [totalPages, page]);

  return (
    <div className="space-y-6 p-6">
      <AttendanceHeader onExportOpen={() => setExportOpen(true)} />

      <KpiCards {...kpi} />

      <LoginRangePanel
        officeCenter={officeCenter}
        rangeCounts={rangeCounts}
        NEAR_KM={NEAR_KM}
        CLOSE_KM={CLOSE_KM}
        MOD_KM={MOD_KM}
      />

      <div className="rounded-2xl border border-gray-200 bg-white p-4 dark:border-white/10 dark:bg-navy-800">
        <div className="mb-3 text-base font-semibold dark:text-white">
          Employee Attendance
        </div>

        <AttendanceFilters
          perPage={perPage}       setPerPage={setPerPage}
          dateFrom={dateFrom}     setDateFrom={setDateFrom}
          dateTo={dateTo}         setDateTo={setDateTo}
          statusFilter={statusFilter} setStatusFilter={setStatusFilter}
          rangeFilter={rangeFilter}   setRangeFilter={setRangeFilter}
          sortBy={sortBy}         setSortBy={setSortBy}
          searchRaw={searchRaw}   setSearchRaw={setSearchRaw}
          onFilterChange={() => setPage(1)}
        />

        <AttendanceTable
          loading={loading}
          pageRows={pageRows}
          sortedDescLength={sortedDesc.length}
          page={page}
          totalPages={totalPages}
          start={start}
          end={page * perPage}
          onPagePrev={() => setPage((p) => Math.max(1, p - 1))}
          onPageNext={() => setPage((p) => Math.min(totalPages, p + 1))}
          onEmployeeClick={(id) => nav(`/admin/hrms/employees/${id}`)}
        />
      </div>

      <ExportModal
        open={exportOpen}
        onClose={() => setExportOpen(false)}
        allRows={sortedDesc}
      />
    </div>
  );
}
